import type { QuoteResultAction } from '@/lib/quoteResultAction';
/**
 * WebSocket Hook
 *
 * Provides real-time updates via Socket.IO connection.
 * Handles authentication, reconnection, and message events.
 *
 * One shared socket per tab so conversation:join and message:new
 * listeners use the same connection (staff-api emits only to the room).
 */

import { useEffect, useRef, useCallback } from 'react';
import { io, type Socket } from 'socket.io-client';
import { useAuthStore } from '@/stores/authStore';
import type { Message } from '@/types';
import type {
  AssistantActionOutcome,
  AssistantFailureReason,
  ConfirmationPresenter,
} from '@/stores/assistantStore';

/** Mirrors `AssistantResponsePayload` in `services/staff-api/src/ws/messaging.ts`. */
export interface AssistantResponseEvent {
  conversationId: string;
  messageId: string;
  content: string;
  type: 'message' | 'action_confirmed' | 'action_cancelled';
  actionId?: string;
  /**
   * `action_confirmed` from the harness path (#2186); `'unknown'` on an
   * `action_cancelled` that may not have stopped an earlier attempt (#2597).
   * Absent on legacy turns.
   */
  outcome?: AssistantActionOutcome;
  /** The settled card's presenter (#2587). Absent on legacy turns. */
  presenter?: ConfirmationPresenter;
  /** A refusal's or failed attempt's reason code (#2587). Absent on legacy turns. */
  failure?: AssistantFailureReason;
  /** #2821, settled quote acceptance: the receipt time that was sent. */
  pinnedReceivedAt?: string;
  /** #2821: a replay of an action completed earlier. */
  alreadyCompleted?: boolean;
  quoteResult?: QuoteResultAction;
}

/** Mirrors `AssistantPendingConfirmationPayload` in the same module. */
export interface AssistantPendingConfirmationEvent {
  conversationId: string;
  messageId: string;
  actionId: string;
  toolName: string;
  description: string;
  params: Record<string, unknown>;
  /** Harness path only (#2180). Absent, the card renders `description`. */
  presenter?: ConfirmationPresenter;
}

/**
 * Small invalidation envelope for insight changes (issue #1090). No insight
 * detail — the client refetches through the permission-checked API.
 */
export interface AssistantInsightUpdatedEvent {
  insightId: string;
  status: string;
}

interface WebSocketEvents {
  // Staff notification bell (PF-135): payload is the list DTO; consumers only invalidate.
  'notification:new': (notification: unknown) => void;
  // Message events
  'message:new': (message: Message) => void;
  'message:status': (data: { messageId: string; status: Message['status'] }) => void;
  'message:delivered': (data: { messageId: string }) => void;
  'message:read': (data: { messageId: string }) => void;

  // Typing indicators
  'typing:start': (data: { conversationId: string; customerName: string }) => void;
  'typing:stop': (data: { conversationId: string }) => void;

  // Assistant events
  'assistant:response': (data: AssistantResponseEvent) => void;
  'assistant:pending_confirmation': (data: AssistantPendingConfirmationEvent) => void;
  'assistant:insight_updated': (data: AssistantInsightUpdatedEvent) => void;

  // Connection events
  'connect': () => void;
  'disconnect': (reason: string) => void;
  'error': (error: Error) => void;
}

interface UseWebSocketOptions {
  /**
   * Base URL for WebSocket server
   * Defaults to / (same origin, Socket.IO uses /socket.io path)
   */
  url?: string;

  /**
   * Enable WebSocket connection
   * Set to false to disable (useful for testing)
   */
  enabled?: boolean;

  /**
   * Reconnection options
   */
  reconnection?: boolean;
  reconnectionDelay?: number;
  reconnectionAttempts?: number;
}

let sharedSocket: Socket | null = null;
let sharedToken: string | null = null;
let sharedUrl: string | null = null;
let socketRefCount = 0;

function acquireSharedSocket(
  token: string,
  url: string,
  options: { reconnection: boolean; reconnectionDelay: number; reconnectionAttempts: number },
): Socket {
  if (
    sharedSocket &&
    (sharedToken !== token || sharedUrl !== url || sharedSocket.disconnected)
  ) {
    sharedSocket.disconnect();
    sharedSocket = null;
    socketRefCount = 0;
  }

  if (!sharedSocket) {
    sharedSocket = io(url, {
      auth: { token },
      reconnection: options.reconnection,
      reconnectionDelay: options.reconnectionDelay,
      reconnectionAttempts: options.reconnectionAttempts,
      transports: ['websocket', 'polling'],
    });
    sharedToken = token;
    sharedUrl = url;
  }

  socketRefCount += 1;
  return sharedSocket;
}

function releaseSharedSocket(): void {
  socketRefCount -= 1;
  if (socketRefCount <= 0 && sharedSocket) {
    sharedSocket.disconnect();
    sharedSocket = null;
    sharedToken = null;
    sharedUrl = null;
    socketRefCount = 0;
  }
}

export function useWebSocket<T extends keyof WebSocketEvents>(
  event: T,
  handler: WebSocketEvents[T],
  options: UseWebSocketOptions = {}
) {
  const {
    url = '/',
    enabled = true,
    reconnection = true,
    reconnectionDelay = 1000,
    reconnectionAttempts = 5,
  } = options;

  const socketRef = useRef<Socket | null>(null);
  const token = useAuthStore((s) => s.accessToken);
  const handlerRef = useRef(handler);

  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  useEffect(() => {
    if (!enabled || !token) {
      return;
    }

    const socket = acquireSharedSocket(token, url, {
      reconnection,
      reconnectionDelay,
      reconnectionAttempts,
    });
    socketRef.current = socket;

    const eventHandler = (data: Parameters<WebSocketEvents[T]>[0]) => {
      (handlerRef.current as (data: Parameters<WebSocketEvents[T]>[0]) => void)(data);
    };

    socket.on(event as string, eventHandler);

    return () => {
      socket.off(event as string, eventHandler);
      socketRef.current = null;
      releaseSharedSocket();
    };
  }, [enabled, token, url, event, reconnection, reconnectionDelay, reconnectionAttempts]);

  const emit = useCallback(
    <E extends string>(eventName: E, data?: unknown) => {
      const socket = socketRef.current;
      if (socket?.connected) {
        socket.emit(eventName, data);
      }
    },
    []
  );

  return {
    socket: socketRef.current,
    connected: socketRef.current?.connected ?? false,
    emit,
  };
}

/**
 * Hook for joining/leaving conversation rooms on the shared socket.
 * Re-joins after reconnect so message:new still reaches this tab.
 */
export function useConversationRoom(conversationId: string | null) {
  const token = useAuthStore((s) => s.accessToken);

  useEffect(() => {
    if (!token || !conversationId) {
      return;
    }

    const socket = acquireSharedSocket(token, '/', {
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
    });

    const join = () => {
      socket.emit('conversation:join', { conversationId });
    };

    if (socket.connected) {
      join();
    }
    socket.on('connect', join);

    return () => {
      socket.off('connect', join);
      socket.emit('conversation:leave', { conversationId });
      releaseSharedSocket();
    };
  }, [token, conversationId]);
}
